import { RangeSlider } from "@carbon6/c6-frontend-design";
import { useEffect, useId, useMemo, useRef, useState, type ReactNode } from "react";
import type { EpisodeDurationFacet, EpisodeListFilters as Filters } from "@/api/datasetEpisode";
import { createDebouncedAction } from "@/utils";
import {
  applyDurationRange,
  durationEdges,
  durationRangeCount,
  formatSeconds,
  readDurationRange,
  type DurationIndexRange,
} from "./episodeDurationFilter";
import styles from "./EpisodeDurationHistogram.module.css";

// 슬라이더를 드래그하는 동안 목록 요청을 묶는 시간(ms). 목록 필터 커밋과 같은 값이다.
// 드래그 중 칸마다 목록과 개수를 다시 받지 않게 한다.
const DURATION_COMMIT_DEBOUNCE_MS = 300;

export type EpisodeDurationHistogramProps = {
  duration: EpisodeDurationFacet;
  value: Filters;
  onChange: (next: Filters) => void;
  disabled?: boolean;
};

/**
 * 에피소드 길이 범위. 칸 막대와, 칸 경계에만 멈추는 범위 슬라이더를 가진다.
 *
 * 막대 높이는 **다른 조건 아래의** 칸별 수(조건부)다. 그래서 조건을 바꾸면 막대 모양이 바뀐다.
 * 칸 경계는 필터 없는 데이터셋에서 정하므로 바뀌지 않는다. 그래서 슬라이더 정지점도 고정이다.
 * 고른 구간의 막대는 진하게 칠한다. 구간과 합은 머리 줄(`10–14초 · N개`)이 표시한다.
 * 그 수는 구간을 적용한 목록의 total 과 같다.
 *
 * 칸이 없으면(모든 에피소드의 길이를 모르면) 그리지 않는다. 칸이 하나면 좁힐 범위가 없으므로
 * 슬라이더를 잠근다. 슬라이더는 드래그 중에도 바로 움직인다(초안).
 * 조건은 드래그가 멈춘 뒤 한 번만 반영한다.
 */
export function EpisodeDurationHistogram({
  duration,
  value,
  onChange,
  disabled = false,
}: EpisodeDurationHistogramProps): ReactNode {
  const labelId = useId();
  const bins = duration.bins;
  const edges = useMemo(() => durationEdges(bins), [bins]);
  const last = edges.length - 1;
  const applied = readDurationRange(value, edges) ?? [0, Math.max(last, 1)];
  const appliedKey = applied.join(":");
  // 경계 값으로 만든 키. facets 를 다시 받으면 배열은 새 객체이지만 경계 값은 같다.
  const edgesKey = edges.join(",");
  const [draft, setDraft] = useState<DurationIndexRange>(applied);
  const commit = useMemo(() => createDebouncedAction(DURATION_COMMIT_DEBOUNCE_MS), []);
  const latestValue = useRef(value);
  latestValue.current = value;
  const latestApplied = useRef(applied);
  latestApplied.current = applied;
  // 마지막으로 내보낸 구간. 부모가 그 값을 다시 돌려주면(메아리) 초안을 바꾸지 않는다.
  const lastCommitted = useRef<string | null>(null);
  const previousEdgesKey = useRef(edgesKey);

  // 바깥에서 조건이 바뀌면(칩 해제, 초기화, 경계가 다른 데이터셋) 초안을 그 조건으로 맞춘다.
  // 그리고 대기 중인 커밋을 버린다. 자기 커밋의 메아리는 무시한다.
  // 그래서 드래그를 계속하는 중에 앞 커밋이 돌아와도 손잡이가 뒤로 이동하지 않는다.
  useEffect(() => {
    const sameEdges = previousEdgesKey.current === edgesKey;
    previousEdgesKey.current = edgesKey;
    if (sameEdges && appliedKey === lastCommitted.current) {
      return;
    }
    commit.cancel();
    setDraft(latestApplied.current);
  }, [appliedKey, edgesKey, commit]);

  useEffect(() => () => commit.cancel(), [commit]);

  if (last < 1) {
    return null;
  }

  const peak = Math.max(1, ...bins.map((bin) => bin.count));
  const whole = draft[0] === 0 && draft[1] === last;
  // 머리 줄이 고른 구간과 그 수를 함께 표시한다. 손잡이 위 값 표시는 끈다.
  // 그 표시는 슬라이더 위아래에 공간을 예약해 좁은 팝오버를 늘린다.
  // 전체 범위이면 길이를 모르는 에피소드도 목록에 남는다.
  const summary = whole
    ? `전체 · ${durationRangeCount(bins, draft) + duration.unspecified}개`
    : `${formatSeconds(edges[draft[0]])}–${formatSeconds(edges[draft[1]])}초 · ${durationRangeCount(bins, draft)}개`;

  function handleChange(next: [number, number]): void {
    setDraft(next);
    commit.schedule(() => {
      lastCommitted.current = next.join(":");
      onChange(applyDurationRange(latestValue.current, edges, next));
    });
  }

  return (
    <div className={styles.root} role="group" aria-labelledby={labelId}>
      <div className={styles.head}>
        <span id={labelId} className={styles.label}>
          길이
        </span>
        <span className={styles.summary}>{summary}</span>
      </div>
      {/* 막대는 슬라이더와 같은 정보를 그림으로 표시한다. 그래서 보조기술에는 숨긴다. */}
      <div className={styles.bars} aria-hidden="true">
        {bins.map((bin, index) => (
          <span
            key={`${bin.min}-${bin.max}`}
            className={styles.bar}
            data-selected={index >= draft[0] && index < draft[1] ? "true" : undefined}
            style={{ height: bin.count === 0 ? 0 : `max(2px, ${(bin.count / peak) * 100}%)` }}
          />
        ))}
      </div>
      <RangeSlider
        value={draft}
        min={0}
        max={last}
        step={1}
        minDistance={1}
        trackSize="sm"
        thumbSize="sm"
        valueLabelDisplay="off"
        getAriaValueText={(index) => `${formatSeconds(edges[index])}초`}
        startAriaLabel="최소 길이"
        endAriaLabel="최대 길이"
        disabled={disabled || last < 2}
        onValueChange={handleChange}
      />
      <div className={styles.edges} aria-hidden="true">
        <span>{formatSeconds(edges[0])}초</span>
        <span>{formatSeconds(edges[last])}초</span>
      </div>
      {/* 길이를 모르는 에피소드는 어떤 구간에도 들지 않는다. 구간을 적용하면 빠지는 수를 미리 표시한다. */}
      {duration.unspecified > 0 && (
        <p className={styles.note}>
          길이를 모르는 에피소드 {duration.unspecified}개는 구간을 좁히면 빠집니다.
        </p>
      )}
    </div>
  );
}
